import React, { useEffect, useRef, useState } from 'react';
import api from '../api';
import './CommunityAvatarsRow.css';

const DISPLAY_LIMIT = 10;
const RETRY_DELAY_MS = 60 * 1000;

function AvatarCard({ creation, onOpen }) {
  const [imageFailed, setImageFailed] = useState(false);

  useEffect(() => {
    setImageFailed(false);
  }, [creation.imageUrl]);

  return (
    <button
      type="button"
      className="community-avatar-card"
      onClick={() => onOpen?.(creation)}
      aria-label={`View ${creation.name || 'avatar'}`}
    >
      <div className="community-avatar-card__art">
        {imageFailed || !creation.imageUrl ? (
          <span className="community-avatar-card__unavailable">
            Image unavailable
          </span>
        ) : (
          <img
            src={creation.imageUrl}
            alt={creation.name || 'Community avatar'}
            loading="lazy"
            decoding="async"
            draggable={false}
            onError={() => setImageFailed(true)}
          />
        )}
      </div>

      <span
        className="community-avatar-card__name"
        title={creation.name}
      >
        {creation.name || 'Untitled avatar'}
      </span>
    </button>
  );
}


function CommunityAvatarsRow({
  user,
  isActive = true,
  onCreateAvatar,
  onOpenAvatar,
  onViewAll,
}) {

  const [creations, setCreations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [retryKey, setRetryKey] = useState(0);

  const scrollRef = useRef(null);
  const accountKey = String(user?._id || user?.username || '');

  useEffect(() => {
    // Clear the previous account's data.
    setCreations([]);
    setLoading(true);
    setError('');
    scrollRef.current?.scrollTo({ left: 0 });
  }, [accountKey]);

  useEffect(() => {
    if (!accountKey || !isActive) return;

    let cancelled = false;
    let inFlight = false;
    let timer;
    let nextFetchAt = 0;

    const controller = new AbortController();

    function schedule() {
      clearTimeout(timer);

      if (cancelled || document.visibilityState === 'hidden') {
        return;
      }

      timer = setTimeout(
        fetchCreations,
        Math.max(1000, nextFetchAt - Date.now())
      );
    }

    async function fetchCreations() {
      if (
        cancelled ||
        inFlight ||
        document.visibilityState === 'hidden'
      ) {
        return;
      }

      inFlight = true;
      setLoading(true);

      try {
        const { data } = await api.get('/community-avatars', {
          signal: controller.signal,
        });

        if (cancelled) return;

        const seen = new Set();

        const cleaned = (
          Array.isArray(data?.creations) ? data.creations : []
        )
          .filter((creation) => {
            if (!creation?._id) return false;

            const id = String(creation._id);
            if (seen.has(id)) return false;

            seen.add(id);
            return true;
          })
          .slice(0, DISPLAY_LIMIT);

        setCreations(cleaned);
        setError('');

        const refreshAt = Date.parse(data?.meta?.nextRefreshAt);

        nextFetchAt =
          Number.isFinite(refreshAt) && refreshAt > Date.now()
            ? refreshAt + 1000
            : Date.now() + RETRY_DELAY_MS;
      } catch (requestError) {
        if (cancelled) return;

        console.error(
          '[CommunityAvatars] Could not load showcase:',
          requestError
        );

        // Clear potentially unavailable creations after a failed
        // refresh instead of keeping stale community cards.
        setCreations([]);
        setError('Could not load community avatars.');

        nextFetchAt = Date.now() + RETRY_DELAY_MS;
      } finally {
        inFlight = false;

        if (!cancelled) {
          setLoading(false);
          schedule();
        }
      }
    }

    function handleVisibilityChange() {
      if (document.visibilityState === 'hidden') {
        clearTimeout(timer);
        return;
      }

      if (Date.now() >= nextFetchAt) {
        fetchCreations();
      } else {
        schedule();
      }
    }

    // Fetch on mount and whenever Home becomes active again.
    fetchCreations();

    document.addEventListener(
      'visibilitychange',
      handleVisibilityChange
    );

    return () => {
      cancelled = true;
      clearTimeout(timer);
      controller.abort();

      document.removeEventListener(
        'visibilitychange',
        handleVisibilityChange
      );
    };
  }, [accountKey, isActive, retryKey]);

  if (!accountKey) return null;

  return (
    <section
      className="community-avatars-row"
      aria-label="Community Avatars"
    >


<div className="community-avatars-row__header">
  <h3>Community Avatars</h3>

  {typeof onViewAll === 'function' && (
    <button
      type="button"
      className="community-avatars-row__view-all"
      onClick={onViewAll}
    >
      View all
    </button>
  )}
</div>


      <div
        ref={scrollRef}
        className="community-avatars-row__scroll"
        aria-busy={loading}
      >
        <button
          type="button"
          className="community-avatar-card community-avatar-card--create"
          onClick={onCreateAvatar}
        >
          <span className="community-avatar-card__art community-avatar-card__create-art">
            <span className="community-avatar-card__plus" aria-hidden="true">
              <svg
                width="32"
                height="32"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M12 5v14M5 12h14"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </span>
          </span>

          <span className="community-avatar-card__name">
            Create avatar
          </span>
        </button>

        {loading && creations.length === 0 && !error
          ? Array.from({ length: 3 }, (_, index) => (
              <div
                key={`skeleton-${index}`}
                className="community-avatar-card community-avatar-card--skeleton"
                aria-hidden="true"
              >
                <div className="community-avatar-card__art community-avatar-card__shimmer" />
                <div className="community-avatar-card__skeleton-label">
                  <span className="community-avatar-card__skeleton-line community-avatar-card__shimmer" />
                </div>
              </div>
            ))
          : creations.map((creation) => (
              <AvatarCard
                key={creation._id}
                creation={creation}
                onOpen={onOpenAvatar}
              />
            ))}
      </div>

      {error && (
        <div className="community-avatars-row__status" role="status">
          <span>{error}</span>

          <button
            type="button"
            disabled={loading}
            onClick={() => setRetryKey((value) => value + 1)}
          >
            {loading ? 'Retrying…' : 'Retry'}
          </button>
        </div>
      )}
    </section>
  );
}

export default CommunityAvatarsRow;
